<template>
  <div>
    <highcharts ref="chart" :options="options"></highcharts>
    <b-notification :closable="false">
      <p>
        * Source
        <a
          target="_blank"
          class="link"
          href="https://www.myco2.fr/actualites/empreinte-carbone-francaise-moyenne-comment-est-elle-calculee"
          >MYCO2</a
        >, 2021.
        <em
          >Empreinte carbone française moyenne, comment est-elle calculée ?</em
        >
      </p>
      <p>
        ** Source
        <a
          target="_blank"
          class="link"
          href="https://www.carbone4.com/files/wp-content/uploads/2019/06/Publication-Carbone-4-Faire-sa-part-pouvoir-responsabilite-climat.pdf"
          >Carbone4</a
        >, 2019.
        <em
          >Faire sa part ? Pouvoir et responsabilité des individus, des
          entreprises et de l'État face à l'urgence climatique.</em
        >
      </p>
    </b-notification>
  </div>
</template>

<script>
import '@/plugins/highcharts.js'
import { CarbonIntensity } from '@/models/carbon/CarbonIntensity.js'
import { useCoreStore } from '@/stores/core.js'

export default {
  name: 'GHGIInformationFR',
  props: {
    simulations: {
      type: Array,
      default: function () {
        return []
      }
    },
    isSimulation: {
      type: Boolean,
      default: false
    },
    citySize: {
      type: Number,
      default: 2
    }
  },
  setup () {
    const coreStore = useCoreStore()
    return { coreStore }
  },
  computed: {
    series: function () {
      let series = [
        {
          name: this.$t('compGraphicsGHGIInformationFR.serie-1'),
          data: [1.4, 0.3],
          color: '#beab9d'
        },
        {
          name: this.$t('compGraphicsGHGIInformationFR.serie-2'),
          data: [2.35, 0.6],
          color: '#f5a342'
        },
        {
          name: this.$t('compGraphicsGHGIInformationFR.serie-3'),
          data: [1.6, 0.6],
          color: '#92a542'
        },
        {
          name: this.$t('compGraphicsGHGIInformationFR.serie-4'),
          data: [1.9, 0.2],
          color: '#80c1f5'
        },
        {
          name: this.$t('compGraphicsGHGIInformationFR.serie-5'),
          data: [2.65, 0.3],
          color: '#00507f'
        }
      ]
      for (let simulation of this.simulations) {
        let cintensity = new CarbonIntensity()
        let fintensity = new CarbonIntensity()
        if (Object.keys(simulation).includes('commute')) {
          cintensity = simulation.commute.getTotalIntensity(
            this.coreStore.options,
            simulation.year,
            this.citySize
          ).intensity
        }
        if (Object.keys(simulation).includes('food')) {
          // returns emissions for 365 * 2 meals if simulation
          fintensity = simulation.food.getCarbonIntensity(
            { NUMBER_OF_WORKED_WEEKS: { default: 52 * 2 } },
            simulation.year
          ).intensity
        }
        series[0].data.push(0)
        series[1].data.push(Math.round((fintensity / 1000) * 100) / 100)
        series[2].data.push(0)
        series[3].data.push(0)
        series[4].data.push(Math.round((cintensity / 1000) * 100) / 100)
      }
      return series
    },
    categories: function () {
      let categories = [
        this.$t('compGraphicsGHGIInformationFR.cat-1'),
        this.$t('compGraphicsGHGIInformationFR.cat-2')
      ]
      for (let simulation of this.simulations) {
        let currentCat =
          this.$t('compGraphicsGHGIInformationFR.label-2') +
          ' (' +
          this.$t('t.co2e') +
          ')'
        if (this.isSimulation) {
          if (Object.keys(simulation).includes('commute')) {
            currentCat = this.getCommuteLabel(
              simulation.commute,
              simulation.year
            )
          } else {
            currentCat = this.getFoodLabel(simulation.food, simulation.year)
          }
        }
        categories.push(currentCat)
      }
      return categories
    },
    options: function () {
      return {
        chart: {
          type: 'bar'
        },
        title: {
          text: ''
        },
        legend: {
          reversed: true
        },
        series: this.series,
        xAxis: {
          categories: this.categories
        },
        yAxis: {
          title: {
            text: ''
          }
        },
        tooltip: {
          valueSuffix: ' ' + this.$t('t.co2e'),
          shared: true
        },
        plotOptions: {
          series: {
            stacking: 'normal',
            dataLabels: {
              enabled: true
            },
            groupPadding: 0,
            pointPadding: 0.1,
            borderWidth: 0
          }
        },
        exporting: {
          enabled: false
        }
      }
    }
  },
  methods: {
    getFoodLabel (food, year) {
      let label = this.$t('compGraphicsGHGIInformationFR.label-1')
      for (let meal of food.meals) {
        label += meal.amount + ' ' + this.$t(meal.type) + ', '
      }
      return label.slice(0, -2) + ' (' + this.$t('t.co2e') + ')'
    },
    getCommuteLabel (commute, year) {
      let label = commute.nWorkingDay1 + ' ' + this.$t('days') + ': '
      for (let section of commute.sections) {
        if (!section.isDay2) {
          label += section.distance + ' ' + this.$t('km-' + section.mode) + ', '
          if (section.mode === 'car') {
            label = label.slice(0, -2)
            label += ' (' + section.engine + '), '
          }
        }
      }
      label = label.slice(0, -2)
      if (commute.hasWorkingDay2) {
        label += ' | ' + commute.nWorkingDay2 + this.$t('days') + ': '
        for (let section of commute.sections) {
          if (section.isDay2) {
            label +=
              section.distance + ' ' + this.$t('km-' + section.mode) + ', '
            if (section.mode === 'car') {
              label = label.slice(0, -2)
              label += ' (' + section.engine + '), '
            }
          }
        }
      }
      return label + ' (' + this.$t('t.co2e') + ')'
    }
  }
}
</script>
